// comissao-search.component.ts
import { Component, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { FormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatSnackBar } from '@angular/material/snack-bar';

import { DefaultComponentsModule } from 'app/shared/modules/default-components.module';
import { TableColumn } from 'app/shared/interfaces';
import { startWith } from 'rxjs';
import { GlobalService } from 'app/shared/services/core';
import { PAGE_SIZE_DEFAULT, PAGE_SIZE_OPTIONS } from 'app/shared/components/_index';
import { ComissaoResponse } from 'app/shared/services/edduca-imob/comissao/response/comissao.response';
import { ComissaoService } from 'app/shared/services/edduca-imob/comissao/comissao.service';
import { AuthService } from 'app/core/auth/auth.service';
import { PerfilEnum } from 'app/shared/models/core/security/perfil.enum';
import { ComissaoStatusEnum, ComissaoStatusLabels, ComissaoStatusColors } from 'app/shared/components/enums/comissao-status.enum';
import { ComprovantePdfModalComponent, ComprovantePdfModalData } from '../modals/comprovante-pdf-modal/comprovante-pdf-modal.component';
import { RealizarPagamentoModalComponent, RealizarPagamentoModalData } from '../modals/realizar-pagamento-modal/realizar-pagamento-modal.component';

@Component({
  selector: 'app-comissao-search',
  templateUrl: './comissao-search.component.html',
  styleUrls: ['./comissao-search.component.scss'],
  imports: [DefaultComponentsModule]
})
export class ComissaoSearchComponent implements OnInit, AfterViewInit {
  filterByNome = this._fb.control('');

  pageSize = PAGE_SIZE_DEFAULT;
  pageSizeOptions: number[] = PAGE_SIZE_OPTIONS;

  dataSource = new MatTableDataSource<ComissaoResponse>();
  
  // Colunas para Parceiro
  columnsParceiro: TableColumn<ComissaoResponse>[] = [
    { label: 'Ações', property: 'actions', type: 'text', visible: true },
    { label: 'Imobiliária', property: 'nomeImobiliaria', type: 'text', visible: true },
    { label: 'CNPJ Imobiliária', property: 'cnpjImobiliaria', type: 'cpfCnpj', visible: true },
    { label: 'Data Pagamento', property: 'data', type: 'text', visible: true },
    { label: 'Valor Comissão', property: 'comissao', type: 'text', visible: true },
    { label: 'Status', property: 'status', type: 'badge', visible: true }
  ];

  // Colunas para ADM_SISTEMA
  columnsAdmin: TableColumn<ComissaoResponse>[] = [
    { label: 'Ações', property: 'actions', type: 'text', visible: true },
    { label: 'Parceiro', property: 'nomeParceiro', type: 'text', visible: true },
    { label: 'Imobiliária', property: 'nomeImobiliaria', type: 'text', visible: true },
    { label: 'CNPJ Imobiliária', property: 'cnpjImobiliaria', type: 'cpfCnpj', visible: true },
    { label: 'Data Pagamento', property: 'data', type: 'text', visible: true },
    { label: 'Valor Comissão', property: 'comissao', type: 'text', visible: true },
    { label: 'Status', property: 'status', type: 'badge', visible: true }
  ];

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  
  menuComissaoAtiva: ComissaoResponse | null = null;

  // Status options para filtro
  statusOptions = [
    { value: null, label: 'Todos' },
    { value: ComissaoStatusEnum.PENDENTE, label: 'Pendente' },
    { value: ComissaoStatusEnum.PAGO, label: 'Pago' },
    { value: ComissaoStatusEnum.CANCELADO, label: 'Cancelado' }
  ];

  // Enum e helpers para status
  readonly ComissaoStatusEnum = ComissaoStatusEnum;
  readonly ComissaoStatusLabels = ComissaoStatusLabels;
  readonly ComissaoStatusColors = ComissaoStatusColors;

  // Perfil do usuário
  isAdmSistema = false;
  isParceiro = false;
  currentUserId: number | null = null;

  get columns(): TableColumn<ComissaoResponse>[] {
    return this.isAdmSistema ? this.columnsAdmin : this.columnsParceiro;
  }

  get visibleColumns(): string[] {
    return this.columns.filter(col => col.visible).map(col => col.property as string);
  }

  filterForm: UntypedFormGroup = this._fb.group({
    dataInicial: [null],
    dataFinal: [null],
    idStatusComissao: [null]
  });

  constructor(
    private _fb: FormBuilder,
    private _comissaoService: ComissaoService,
    private _authService: AuthService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    public gs: GlobalService,
  ) {}

  ngOnInit(): void {
    this.initUserProfile();
    this.initFilters();
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  private initUserProfile(): void {
    const tokenInfo = this._authService.getTokenInfo();
    if (tokenInfo) {
      this.currentUserId = tokenInfo.userId || tokenInfo.UserId || tokenInfo.user_id;
      const perfilId = tokenInfo.perfilId || tokenInfo.PerfilId || tokenInfo.perfil_id || tokenInfo.perfilid;
      this.isAdmSistema = Number(perfilId) === PerfilEnum.ADM_SISTEMA;
      this.isParceiro = Number(perfilId) === PerfilEnum.PARCEIRO;
    }
  }

  private initFilters(): void {
    this.filterByNome.valueChanges.pipe(startWith('')).subscribe(() => this.applyFilters());
    this.dataSource.filterPredicate = () => true;
  }

  async search(): Promise<void> {
    const filtro = this.filterForm.value;
    
    try {
      let raw: ComissaoResponse[];
      
      if (this.isAdmSistema) {
        // ADM_SISTEMA vê todas as comissões
        raw = await this._comissaoService.obtemTodasComissoes({
          dataInicial: filtro.dataInicial,
          dataFinal: filtro.dataFinal,
          idStatusComissao: filtro.idStatusComissao
        });
      } else {
        // Parceiro vê apenas suas comissões
        raw = await this._comissaoService.obtemComissaoFiltrada({
          idUsuario: this.currentUserId!,
          dataInicial: filtro.dataInicial,
          dataFinal: filtro.dataFinal,
          idStatusComissao: filtro.idStatusComissao
        });
      }
      
      const list = raw.map(r => new ComissaoResponse(r));
      this.dataSource.data = list;
      this.applyFilters();
    } catch (err) {
      console.error('Erro ao buscar comissões', err);
      this.snackBar.open('Erro ao buscar comissões', 'Fechar', { duration: 3000 });
    }
  }

  limparFiltros(): void {
    this.filterForm.reset();
    this.filterByNome.reset();
    this.dataSource.data = [];
  }

  // ========== AÇÕES DO PARCEIRO E ADM ==========
  
  /**
   * Ver comprovante (disponível para comissões PAGAS)
   */
  async verComprovante(): Promise<void> {
    if (!this.menuComissaoAtiva) return;
    
    // Verifica se a comissão está paga
    if (this.menuComissaoAtiva.idStatusComissao !== ComissaoStatusEnum.PAGO) {
      this.snackBar.open('Comprovante disponível apenas para comissões pagas', 'Fechar', { duration: 3000 });
      return;
    }

    try {
      // Busca o comprovante do backend
      const response = await this._comissaoService.obterComprovante(this.menuComissaoAtiva.id);
      
      if (!response.comprovanteBase64) {
        this.snackBar.open('Comprovante não encontrado', 'Fechar', { duration: 3000 });
        return;
      }

      const data: ComprovantePdfModalData = {
        titulo: `Comprovante - ${this.menuComissaoAtiva.nomeImobiliaria || 'Comissão #' + this.menuComissaoAtiva.id}`,
        comprovanteBase64: response.comprovanteBase64,
        nomeArquivo: `comprovante_comissao_${this.menuComissaoAtiva.id}.pdf`
      };

      this.dialog.open(ComprovantePdfModalComponent, {
        data,
        width: '800px',
        maxWidth: '90vw',
        height: '80vh',
        panelClass: 'comprovante-pdf-modal'
      });
    } catch (err) {
      console.error('Erro ao carregar comprovante:', err);
      this.snackBar.open('Erro ao carregar comprovante', 'Fechar', { duration: 3000 });
    }
  }

  // ========== AÇÕES DO ADM_SISTEMA ==========
  
  /**
   * Realizar pagamento (disponível para comissões PENDENTES)
   */
  realizarPagamento(): void {
    if (!this.menuComissaoAtiva) return;
    
    // Verifica se a comissão está pendente
    if (this.menuComissaoAtiva.idStatusComissao !== ComissaoStatusEnum.PENDENTE) {
      this.snackBar.open('Pagamento disponível apenas para comissões pendentes', 'Fechar', { duration: 3000 });
      return;
    }

    const data: RealizarPagamentoModalData = {
      idComissao: this.menuComissaoAtiva.id,
      nomeParceiro: this.menuComissaoAtiva.nomeParceiro || 'Não informado',
      emailParceiro: this.menuComissaoAtiva.emailParceiro || 'Não informado',
      chavePix: this.menuComissaoAtiva.chavePix || 'Não informado',
      valorComissao: this.menuComissaoAtiva.valorComissao,
      nomeImobiliaria: this.menuComissaoAtiva.nomeImobiliaria || 'Não informado'
    };

    this.dialog.open(RealizarPagamentoModalComponent, {
      data,
      width: '500px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      disableClose: true,
      panelClass: 'realizar-pagamento-modal'
    }).afterClosed().subscribe(result => {
      if (result === true) {
        this.search(); // Recarrega os dados
      }
    });
  }

  // ========== HELPERS ==========

  getStatusLabel(status: number): string {
    return ComissaoStatusLabels[status] || 'Desconhecido';
  }

  getStatusColor(status: number): string {
    return ComissaoStatusColors[status] || 'bg-gray-500/20 text-gray-500';
  }

  canViewComprovante(row: ComissaoResponse): boolean {
    return row.idStatusComissao === ComissaoStatusEnum.PAGO;
  }

  canRealizarPagamento(row: ComissaoResponse): boolean {
    return this.isAdmSistema && row.idStatusComissao === ComissaoStatusEnum.PENDENTE;
  }

  private applyFilters(): void {
    const search = this.filterByNome.value?.toLowerCase() ?? '';
    
    this.dataSource.filterPredicate = (d: ComissaoResponse) => {
      if (!search) return true;
      const nomeMatch = this.isAdmSistema 
        ? (d.nomeParceiro?.toLowerCase().includes(search) || d.nomeImobiliaria?.toLowerCase().includes(search))
        : d.nomeImobiliaria?.toLowerCase().includes(search);
      const cnpjMatch = d.cnpjImobiliaria?.replace(/\D/g, '').includes(search.replace(/\D/g, ''));
      return !!(nomeMatch || cnpjMatch);
    };
    
    this.dataSource.filter = Math.random().toString();
  }
}
